import { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { RouterProps, withRouter } from 'react-router-dom'
import Folder from 'components/Folder/Folder'
import MasteredWords from 'components/MasteredWords/MasteredWords'
import ScoreIndicator from 'components/ScoreIndicator/ScoreIndicator'
import StudySetMenuModal from 'components/StudySetMenuModal/StudySetMenuModal'
import StudySetsCarousel from 'components/StudySetsCarousel/StudySetsCarousel'
import useThunkDispatch from 'hooks/useThunkDispatch'
import usePagination from 'hooks/usePagination'
import { ReactComponent as PlusIcon } from 'resources/graphics/icons/plus.svg'
import { routes } from 'routes/RouterStackComponent'
import {
  getDifficultWordsAndMasteredCount,
  getStudySets,
  selectDifficultWordsAndMasteredCount,
  selectStudySets,
  setCurrentStudySetId,
  SimpleWordScore
} from 'store/services/Exercise'
import { allSettled } from 'utils/helpers'

import styles from './Pronunciation.module.scss'

const Pronunciation: React.FC<RouterProps> = (props) => {
  const dispatch = useThunkDispatch()
  const masteredWords = usePagination<SimpleWordScore>('/difficult-words/mastered', 10)

  const { difficultWords, masteredCount } = useSelector(selectDifficultWordsAndMasteredCount)
  const studySets = useSelector(selectStudySets)
  const [isLoading, setIsLoading] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const hasLoadedOnce = useRef(false)

  const onNeedsPracticeClick = () => {
    props.history.push(`${routes.reading_level_up}/needs_practice_set`)
  }

  const onStudySetClick = (id: string | null) => {
    dispatch(setCurrentStudySetId(id))
    setIsMenuOpen(true)
  }

  const onCreateStudySetClick = () => {
    setIsMenuOpen(true)
  }

  const renderWordsInColumn = (wordsToRender: SimpleWordScore[], renderCount?: number) => {
    const words = renderCount ? wordsToRender.slice(0, renderCount) : wordsToRender

    return words.map((word, index) => (
      <div className={styles.rowWrapper} key={`${word.word}${index}`}>
        <div className={`${styles.word} ${word.word.length > 8 ? styles.long : styles.short}`}>{word.word}</div>

        <ScoreIndicator
          value={word.score}
          sizeInPx={42}
          strokeWidthInPx={7}
          disableAnimation
          fontSize={word.score === 100 ? '13px' : '16px'}
          colorsThresholds={{
            LOW: 50,
            HIGH: 80
          }}
        />
      </div>
    ))
  }

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.currentTarget

      const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20

      if (reachedBottom) {
        masteredWords.fetchData()
      }
    },
    [masteredWords]
  )

  useEffect(() => {
    ;(async () => {
      try {
        setIsLoading(true)

        dispatch(setCurrentStudySetId(null))
        await allSettled([dispatch(getDifficultWordsAndMasteredCount()), dispatch(getStudySets())])
        await masteredWords.fetchData()
      } finally {
        hasLoadedOnce.current = true
        setIsLoading(false)
      }
    })()
  }, [])

  return (
    <>
      <div className={styles.container} onScroll={onScroll}>
        <div className={styles.innerContainer}>
          <section className={styles.sectionWrapper}>
            <div className={styles.headingWrapper}>
              <h2 className={styles.headingText}>Needs practice</h2>
            </div>
            {isLoading && !difficultWords.length ? (
              <div className={styles.dashedContainer}>
                <div className={styles.loader}>
                  <div
                    className={styles.spinner}
                    style={{
                      borderTopColor: '#78C7F5'
                    }}
                  />
                </div>
              </div>
            ) : difficultWords.length === 0 ? (
              <div className={styles.dashedContainer}>
                <div className={styles.noWordsYet}>- No challenging words yet -</div>
              </div>
            ) : (
              <button
                type="button"
                aria-label={'Start practice'}
                className={styles.clickable}
                onClick={onNeedsPracticeClick}
              >
                <Folder title="Astrid" color="red">
                  <div className={styles.helperContainer}>
                    {renderWordsInColumn(difficultWords, 2)}
                    <div
                      className={styles.badge}
                      style={{
                        backgroundColor: '#f76988'
                      }}
                    >
                      <span className={styles.badgeText}>
                        {Math.min(difficultWords.length, 5)} word
                        {difficultWords.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>
                </Folder>
              </button>
            )}
          </section>
          <section className={styles.sectionWrapper}>
            <div className={styles.headingWrapper}>
              <h2 className={styles.headingText}>My words</h2>
              <button
                type="button"
                aria-label={'Create study set'}
                className={styles.clickable}
                onClick={onCreateStudySetClick}
              >
                <div className={styles.createButtonWrapper}>
                  <PlusIcon fill={'#18ACB7'} width={14} height={14} />
                  <span className={styles.actionButton}>Create</span>
                </div>
              </button>
            </div>
            {isLoading && !studySets.length && !hasLoadedOnce.current ? (
              <div className={styles.dashedContainer}>
                <div className={styles.loader}>
                  <div
                    className={styles.spinner}
                    style={{
                      borderTopColor: '#78C7F5'
                    }}
                  />
                </div>
              </div>
            ) : studySets.length === 0 ? (
              <button
                type="button"
                aria-label={'Create study set'}
                className={styles.clickable}
                onClick={onCreateStudySetClick}
              >
                <Folder title="Create" color="mint" childrenBackgroundColor={'#26d4dd'} shadowColor={'#00A1AA'}>
                  <div className={styles.studySetPlaceholderBody}>
                    <PlusIcon fill={'#00A1AA'} width={90} height={90} />
                  </div>
                </Folder>
              </button>
            ) : (
              <StudySetsCarousel studySets={studySets} onStudySetClick={onStudySetClick} />
            )}
          </section>
          <div className={styles.divider} />
          <section className={styles.sectionWrapper}>
            <div className={styles.headingWrapper}>
              <h2 className={styles.headingText}>Mastered ({masteredCount})</h2>
            </div>
            {masteredWords.loading && !masteredWords.data ? (
              <div className={styles.dashedContainer}>
                <div className={styles.loader}>
                  <div
                    className={styles.spinner}
                    style={{
                      borderTopColor: '#78C7F5'
                    }}
                  />
                </div>
              </div>
            ) : masteredCount === 0 ? (
              <div className={styles.dashedContainer}>
                <div className={styles.noWordsYet}>- No mastered words yet -</div>
              </div>
            ) : (
              <MasteredWords masteredWords={masteredWords.data} />
            )}
          </section>
        </div>
      </div>
      {isMenuOpen ? (
        <StudySetMenuModal
          onClose={() => {
            setIsMenuOpen(false)
            dispatch(setCurrentStudySetId(null))
          }}
        />
      ) : null}
    </>
  )
}

export default withRouter(Pronunciation)
